import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Spin, Table, Row, Col, Select, Button, Space, Typography } from 'antd';
import axios from 'axios';
import Utils from '../../../misc/Utils';
import { getReportFamily, resolveEMSSummaryConfig, resolveMTConfig, resolveSHSConfig } from './reportConfigResolver';

const { Text } = Typography;
const { Option } = Select;

const PROFICIENCY_OPTIONS = [
   { value: 'beginner', label: 'Beginner' },
   { value: 'basic', label: 'Basic' },
   { value: 'competent', label: 'Competent' }
];

function normalize(value) {
   return (value || '').toString().trim().toLowerCase();
}

function formatStudentName(value) {
   const raw = (value || '').toString().trim();
   if (!raw) return '';
   if (raw.includes(',')) return raw;

   const parts = raw.split(/\s+/).filter(Boolean);
   if (parts.length <= 1) return raw;

   const lastName = parts.pop();
   const firstName = parts.join(' ');
   return `${lastName}, ${firstName}`;
}

function getAssessmentId(assignmentId, fallbackAssessmentId) {
   return assignmentId !== undefined ? assignmentId : fallbackAssessmentId;
}

function resolveSubjectContexts(template, subjectId) {
   const family = getReportFamily(template, subjectId);
   const normalizedSubject = normalize(subjectId);

   if (family === 'ems') {
      const config = resolveEMSSummaryConfig(template);
      if (!config) {
         return [];
      }

      const availableSubjects = ['english', 'math', 'science'];
      const subjects = (!normalizedSubject || normalizedSubject === 'ems')
         ? availableSubjects
         : availableSubjects.filter((item) => item === normalizedSubject);

      return subjects
         .filter((item) => config[item])
         .map((item) => ({
            key: item,
            label: item.toUpperCase(),
            config: config[item],
            family
         }));
   }

   if (family === 'mt') {
      const config = resolveMTConfig(template, subjectId);
      return config ? [{
         key: normalizedSubject || 'mt',
         label: normalizedSubject.toUpperCase(),
         config,
         family
      }] : [];
   }

   const config = resolveSHSConfig(template, subjectId);
   return config ? [{
      key: normalizedSubject || 'shs',
      label: (subjectId || 'SHS').toString().toUpperCase(),
      config,
      family
   }] : [];
}

function getSummaryDataUrl(props, context) {
   const aid = getAssessmentId(props.assignment_id, props.assessment_id);
   const apiBase = process.env.REACT_APP_API_BRAINEE_DIAGNOSTIC;

   if (props.version === 1) {
      return `${apiBase}/getdiagnosticreportdata?aid=${aid}&sid=${props.section_id}`;
   }

   if (context.family === 'mt' && context.config.summaryApiVersion === 3) {
      return `${apiBase}/v3/getdiagnosticreportdata?aid=${aid}&paid=${props.pre_assignment_id}&sids=${props.section_id}&s=${props.subject_id}&sy=${props.school_year}`;
   }

   if (context.family === 'shs' && context.config.apiVersion === 2) {
      return `${apiBase}/v2/getdiagnosticreportdata?aid=${aid}&sids=${props.section_id}&s=${props.subject_id}&sy=${props.school_year}`;
   }

   return `${apiBase}/v3/getdiagnosticreportdata?aid=${aid}&paid=${props.pre_assignment_id}&sids=${props.section_id}&s=${props.subject_id}&sy=${props.school_year}`;
}

function getTotalScore(row, summaryAreas) {
   return summaryAreas.reduce((sum, area) => {
      const areaSum = Array.from({ length: area.count }, (_, offset) => {
         const field = `s${area.start + offset}`;
         const value = Number(row[field]);
         return Number.isNaN(value) ? 0 : value;
      }).reduce((a, b) => a + b, 0);

      return sum + areaSum;
   }, 0);
}

function getProficiency(totalScore, totalRanges) {
   const match = (totalRanges || []).find(([min, max]) => totalScore >= min && totalScore <= max);
   return match?.[2] || '';
}

function buildStudentRows(data, contexts, diagnosticType) {
   const rowsByKey = new Map();
   contexts.forEach((context) => {
      const rowsForSubject = context.family === 'ems'
         ? data.filter((item) => normalize(item.subject) === context.key)
         : data;

      rowsForSubject.forEach((item) => {
         const totalScore = getTotalScore(item, context.config.summaryAreas);
         const proficiency = getProficiency(totalScore, context.config.totalRanges);
         const sectionValue = item.section || item.section_desc || item.section_name || '';
         const diagnosticTypeValue = normalize(item.diagnostic_type) || normalize(diagnosticType) || 'pre';
         const baseKey = `${context.key}-${item.student_id || item.uid || item.id || normalize(item.student_name)}-${sectionValue}`;

         if (!rowsByKey.has(baseKey)) {
            rowsByKey.set(baseKey, {
               key: baseKey,
               student_name: item.student_name,
               student_name_display: formatStudentName(item.student_name),
               grade_level: item.grade_level,
               section: sectionValue,
               subject: context.key,
               subjectLabel: context.label,
               pre_total_score: null,
               pre_proficiency: '',
               post_total_score: null,
               post_proficiency: '',
            });
         }

         const row = rowsByKey.get(baseKey);
         if (diagnosticTypeValue === 'post') {
            row.post_total_score = totalScore;
            row.post_proficiency = proficiency;
         } else {
            row.pre_total_score = totalScore;
            row.pre_proficiency = proficiency;
         }
      });
   });

   const rows = Array.from(rowsByKey.values());
   return rows.sort((a, b) => {
      const sectionCompare = (a.section || '').localeCompare(b.section || '');
      if (sectionCompare !== 0) return sectionCompare;
      return (a.student_name_display || '').localeCompare(b.student_name_display || '');
   });
}

export function DiagnosticCompetencyListReport(props) {
   const {
      assignment_id,
      assessment_id,
      pre_assignment_id,
      section_id,
      school_year,
      version,
      diagnostic_type,
      template,
      subject_id
   } = props;

   const [loading, setLoading] = useState(false);
   const [reportData, setReportData] = useState([]);
   const [generatedRows, setGeneratedRows] = useState([]);
   const [selectedSection, setSelectedSection] = useState('all');
   const [selectedPreProficiencies, setSelectedPreProficiencies] = useState([]);
   const [selectedPostProficiencies, setSelectedPostProficiencies] = useState([]);

   const subjectContexts = useMemo(
      () => resolveSubjectContexts(template, subject_id),
      [template, subject_id]
   );

   const sectionOptions = useMemo(() => {
      const uniqueSections = Array.from(new Set(
         reportData.map((item) => item.section).filter(Boolean)
      ));

      return [
         { value: 'all', label: 'All Sections' },
         ...uniqueSections.map((section) => ({ value: section, label: section }))
      ];
   }, [reportData]);

   const totalItemsLabel = useMemo(() => {
      if (!subjectContexts.length) return '0';

      const itemTotals = subjectContexts.map((context) => ({
         label: context.label,
         count: (context.config.summaryAreas || []).reduce((sum, area) => sum + (area.count || 0), 0)
      }));

      if (itemTotals.length === 1) {
         return `${itemTotals[0].count}`;
      }

      return itemTotals.map((item) => `${item.label}: ${item.count}`).join(' | ');
   }, [subjectContexts]);

   const competencySummary = useMemo(() => {
      const summary = {
         pre: { beginner: 0, basic: 0, competent: 0 },
         post: { beginner: 0, basic: 0, competent: 0 }
      };

      generatedRows.forEach((item) => {
         const pre = normalize(item.pre_proficiency);
         const post = normalize(item.post_proficiency);

         if (summary.pre[pre] !== undefined) summary.pre[pre] += 1;
         if (summary.post[post] !== undefined) summary.post[post] += 1;
      });

      return summary;
   }, [generatedRows]);

   const diagnosticTitle = props.diagnostic_title || '';

   useEffect(() => {
      Utils.verifyJwt();

      if (!subjectContexts.length) {
         setReportData([]);
         return;
      }

      setLoading(true);
      axios.get(getSummaryDataUrl({
         assignment_id,
         assessment_id,
         pre_assignment_id,
         section_id,
         subject_id,
         school_year,
         version
      }, subjectContexts[0])).then((response) => {
         const data = response.data || [];
         const rows = buildStudentRows(data, subjectContexts, diagnostic_type);
         setReportData(rows);
         setGeneratedRows(rows);
         setLoading(false);
      }, () => setLoading(false));
   }, [assignment_id, assessment_id, pre_assignment_id, section_id, subject_id, school_year, version, diagnostic_type, subjectContexts]);

   const handleGenerate = () => {
      const nextRows = reportData.filter((item) => {
         const normalizedPreProficiency = normalize(item.pre_proficiency);
         const normalizedPostProficiency = normalize(item.post_proficiency);
         const matchesSection = selectedSection === 'all' || item.section === selectedSection;
         const matchesPreProficiency = selectedPreProficiencies.length === 0 || selectedPreProficiencies.includes(normalizedPreProficiency);
         const matchesPostProficiency = selectedPostProficiencies.length === 0 || selectedPostProficiencies.includes(normalizedPostProficiency);
         
         return matchesSection && matchesPreProficiency && matchesPostProficiency;
      }).sort((a, b) => {
         const sectionCompare = (a.section || '').localeCompare(b.section || '');
         
         if (sectionCompare !== 0) return sectionCompare;
         
         return (a.student_name_display || '').localeCompare(b.student_name_display || '');
      });

      setGeneratedRows(nextRows);
   };

   const columns = [
      {
         title: 'Student',
         dataIndex: 'student_name_display',
         key: 'student_name',
         width: '35%',
      },
      // {
      //    title: 'Grade Level',
      //    dataIndex: 'grade_level',
      //    key: 'grade_level',
      //    width: '17%',
      // },
      {
         title: 'Section',
         dataIndex: 'section',
         key: 'section',
         width: '25%',
      },
      {
         title: 'Pre-Test Score',
         dataIndex: 'pre_total_score',
         key: 'pre_total_score',
         width: '10%',
         align: 'center',
         render: (value) => value ?? '-',
      },
      {
         title: 'Pre-Test Competency',
         dataIndex: 'pre_proficiency',
         key: 'pre_proficiency',
         width: '15%',
         render: (value) => value || '-',
      },
      {
         title: 'Post-Test Score',
         dataIndex: 'post_total_score',
         key: 'post_total_score',
         width: '10%',
         align: 'center',
         render: (value) => value ?? '-',
      },
      {
         title: 'Post-Test Competency',
         dataIndex: 'post_proficiency',
         key: 'post_proficiency',
         width: '15%',
         render: (value) => value || '-',
      }
   ];

   if (loading) {
      return <div className='center-empty-space'><Spin size='large' tip='Loading competency list, please wait...' /></div>;
   }

   if (!subjectContexts.length) {
      return <div className='center-empty-space'><Empty description='Report not yet available.' image='../images/brainee_mascot.png' /></div>;
   }

   if (!reportData.length) {
      return <div className='center-empty-space'><Empty description='No Data Found' image='../images/brainee_mascot.png' /></div>;
   }

   return (
      <div
         className='dashboard-item-inner3'
         style={{ margin: '20px' }}
         id='competency-list-report'
         data-diagnostic-title={diagnosticTitle || ''}
         data-total-items={totalItemsLabel || ''}
      >
         <Row gutter={[12, 12]} style={{ marginBottom: '15px' }} className='competency-filter-panel'>
            {/* <Col span={24}>
               <Text strong>Student List by Overall Competency</Text>
            </Col> */}
            <Col xs={24} md={6}>
               <Select
                  value={selectedSection}
                  style={{ width: '100%' }}
                  onChange={setSelectedSection}
               >
                  {sectionOptions.map((item) => (
                     <Option key={item.value} value={item.value}>{item.label}</Option>
                  ))}
               </Select>
            </Col>
            <Col xs={24} md={6}>
               <Select
                  mode='multiple'
                  allowClear
                  placeholder='Pre-test proficiency'
                  value={selectedPreProficiencies}
                  style={{ width: '100%' }}
                  onChange={setSelectedPreProficiencies}
               >
                  {PROFICIENCY_OPTIONS.map((item) => (
                     <Option key={item.value} value={item.value}>{item.label}</Option>
                  ))}
               </Select>
            </Col>
            <Col xs={24} md={6}>
               <Select
                  mode='multiple'
                  allowClear
                  placeholder='Post-test proficiency'
                  value={selectedPostProficiencies}
                  style={{ width: '100%' }}
                  onChange={setSelectedPostProficiencies}
               >
                  {PROFICIENCY_OPTIONS.map((item) => (
                     <Option key={item.value} value={item.value}>{item.label}</Option>
                  ))}
               </Select>
            </Col>
            <Col xs={24} md={6}>
               <Space>
                  <Button type='primary' shape='round' onClick={handleGenerate}>Show Report</Button>
                  {/* <Text type='secondary'>{generatedRows.length} student(s) found</Text> */}
               </Space>
            </Col>
         </Row>

         <Table
            size='small'
            columns={columns}
            dataSource={generatedRows}
            rowKey='key'
            pagination={false}
            className='competency-list-table'
            scroll={{
               x: '800px',
               y: 'calc(100vh - 32rem)',
            }}
            footer={() => (
               <div>
                  <div style={{ marginTop: 8, overflowX: 'auto' }}>
                     <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 320 }}>
                        <thead>
                           <tr>
                              <th style={{ textAlign: 'left', border: '1px solid #B0F2FC', padding: '6px 8px', backgroundColor: '#B0F2FC' }}>Proficiency</th>
                              <th style={{ textAlign: 'center', border: '1px solid #B0F2FC', padding: '6px 8px', backgroundColor: '#B0F2FC' }}>Pre-Test</th>
                              <th style={{ textAlign: 'center', border: '1px solid #B0F2FC', padding: '6px 8px', backgroundColor: '#B0F2FC' }}>Post-Test</th>
                           </tr>
                        </thead>
                        <tbody>
                           <tr>
                              <td style={{ border: '1px solid #f0f0f0', padding: '6px 8px' }}>Beginner</td>
                              <td style={{ textAlign: 'center', border: '1px solid #f0f0f0', padding: '6px 8px' }}>{competencySummary.pre.beginner}</td>
                              <td style={{ textAlign: 'center', border: '1px solid #f0f0f0', padding: '6px 8px' }}>{competencySummary.post.beginner}</td>
                           </tr>
                           <tr>
                              <td style={{ border: '1px solid #f0f0f0', padding: '6px 8px' }}>Basic</td>
                              <td style={{ textAlign: 'center', border: '1px solid #f0f0f0', padding: '6px 8px' }}>{competencySummary.pre.basic}</td>
                              <td style={{ textAlign: 'center', border: '1px solid #f0f0f0', padding: '6px 8px' }}>{competencySummary.post.basic}</td>
                           </tr>
                           <tr>
                              <td style={{ border: '1px solid #f0f0f0', padding: '6px 8px' }}>Competent</td>
                              <td style={{ textAlign: 'center', border: '1px solid #f0f0f0', padding: '6px 8px' }}>{competencySummary.pre.competent}</td>
                              <td style={{ textAlign: 'center', border: '1px solid #f0f0f0', padding: '6px 8px' }}>{competencySummary.post.competent}</td>
                           </tr>
                        </tbody>
                     </table>
                  </div>
                  <Text>Total Records: {generatedRows.length}</Text>
               </div>
            )}
         />
      </div>
   );
}
